import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { X, Plus, Trash2, AlertTriangle, CalendarOff } from "lucide-react";
import { toast } from "sonner";

// Format a UTC ISO string as a local YYYY-MM-DD / HH:mm pair for the
// <input type="date"> and <input type="time"> controls.
function splitIsoLocal(iso) {
    if (!iso) return { date: "", time: "09:00" };
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { date: "", time: "09:00" };
    const pad = (n) => String(n).padStart(2, "0");
    return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
}

// Build a list of "HH:MM" slots in 15-minute increments between start and end
// hours (inclusive of start, exclusive of end + 1 slot at end:00). Used to
// constrain the Start-time picker so operatives can't accidentally pick
// awkward minute values like 09:07.
function buildTimeSlots(startHour = 5, endHour = 22) {
    const pad = (n) => String(n).padStart(2, "0");
    const slots = [];
    for (let h = startHour; h <= endHour; h++) {
        for (const m of [0, 15, 30, 45]) {
            if (h === endHour && m > 0) break;
            slots.push(`${pad(h)}:${pad(m)}`);
        }
    }
    return slots;
}
const TIME_SLOTS = buildTimeSlots(5, 22);

// Snap an arbitrary "HH:MM" to the nearest 15-minute slot within TIME_SLOTS.
function snapToSlot(t) {
    if (!t) return "09:00";
    const [hStr, mStr] = t.split(":");
    const h = Number(hStr);
    const m = Number(mStr);
    if (Number.isNaN(h) || Number.isNaN(m)) return "09:00";
    const snappedM = Math.round(m / 15) * 15;
    const pad = (n) => String(n).padStart(2, "0");
    let candidate = snappedM === 60 ? `${pad(h + 1)}:00` : `${pad(h)}:${pad(snappedM)}`;
    if (TIME_SLOTS.includes(candidate)) return candidate;
    // Fall back to a slot within range
    if (h < 5) return TIME_SLOTS[0];
    if (h > 22) return TIME_SLOTS[TIME_SLOTS.length - 1];
    return candidate;
}

export default function CreateJobDialog({ open, onClose, onCreated, onUpdated, operatives, job, pipelineMode = false, wipMode = false, bookInMode = false }) {
    const { user: currentUser } = useAuth();
    const isEdit = Boolean(job);

    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [title, setTitle] = useState("");
    const [client, setClient] = useState("");
    const [address, setAddress] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("09:00");
    const [duration, setDuration] = useState(90);
    // Multi-day span: on CREATE only, fans this job out into N consecutive
    // daily cards. 1 = single day (default). Hidden when recurring/pipeline
    // is on, and hidden entirely in edit mode (each day is its own job so
    // extending an existing span is done by creating another).
    const [daysSpan, setDaysSpan] = useState(1);
    const [priority, setPriority] = useState("normal");
    const [assignedTo, setAssignedTo] = useState("");
    const [helperIds, setHelperIds] = useState([]);
    const [notes, setNotes] = useState("");
    const [notes2, setNotes2] = useState("");
    const [checklist, setChecklist] = useState([]);
    // Per-item target counts; absent entries default to 1 at save/display time.
    const [checklistCounts, setChecklistCounts] = useState({});
    // Items (library + custom) that require a proof photo before ticking.
    const [checklistPhotoRequired, setChecklistPhotoRequired] = useState([]);
    // Workspace-wide checklist library (seeded server-side). Operatives
    // can only clock out once every attached item is ticked, so the aim
    // here is a snappy "pick what applies, add anything custom" flow.
    const [libraryItems, setLibraryItems] = useState([]);
    // Items in the library that are marked as "photo required" by the manager.
    // When a job picks one of these the operative must attach a photo to tick.
    const [libraryPhotoRequired, setLibraryPhotoRequired] = useState([]);
    const [customDraft, setCustomDraft] = useState("");
    const [recurrence, setRecurrence] = useState("none");
    // Selected weekdays for recurrence_kind === "custom_weekdays".
    // Python convention: Mon=0..Sun=6.
    const [recurrenceWeekdays, setRecurrenceWeekdays] = useState([]);
    // Pipeline mode: a "no date yet" placeholder. Managers use this to
    // capture leads/enquiries before a firm time is agreed. Editing a
    // pipeline job with a date filled in will graduate it to the schedule.
    const [isPipeline, setIsPipeline] = useState(false);
    const [pipelineReason, setPipelineReason] = useState("");
    const [isWip, setIsWip] = useState(false);
    const offSchedule = isPipeline || isWip;
    const [isOfficeCleaning, setIsOfficeCleaning] = useState(false);
    // Visibility: who else in the workspace can see this job. `team_only`
    // means just the assignee + helpers + workspace owner (Colin).
    const [visibilityMode, setVisibilityMode] = useState("team_only");
    const [visibleTo, setVisibleTo] = useState([]);
    const [busy, setBusy] = useState(false);
    const [applyToSeries, setApplyToSeries] = useState(false);
    const [clashes, setClashes] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [clashChecking, setClashChecking] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/clients").then(r => setClients(r.data)).catch(() => setClients([]));
        // Pull the workspace checklist library; auto-seeds defaults on first read.
        api.get("/checklist-library").then(r => {
            setLibraryItems(r.data?.items || []);
            setLibraryPhotoRequired(r.data?.photo_required_items || []);
        }).catch(() => { setLibraryItems([]); setLibraryPhotoRequired([]); });

        if (isEdit) {
            const { date: d, time: t } = splitIsoLocal(job.scheduled_at);
            setClientId(job.client_id || "");
            setTitle(job.title || "");
            setClient(job.client_name || "");
            setAddress(job.address || "");
            setDate(d);
            setTime(snapToSlot(t || "09:00"));
            setDuration(job.duration_minutes || 90);
            setPriority(job.priority || "normal");
            setAssignedTo(job.assigned_to || "");
            setHelperIds(job.helper_ids || []);
            setNotes(job.notes || "");
            setNotes2(job.notes2 || "");
            setChecklist(job.checklist?.length ? [...job.checklist] : []);
            setChecklistCounts({ ...(job.checklist_counts || {}) });
            setChecklistPhotoRequired([...(job.checklist_photo_required || [])]);
            // Prefer the new richer kind, else derive from legacy recurrence_days
            const kindFromDays = { 1: "daily", 7: "weekly", 14: "fortnightly", 30: "monthly" };
            setRecurrence(job.recurrence_kind || kindFromDays[job.recurrence_days] || "none");
            setRecurrenceWeekdays(Array.isArray(job.recurrence_weekdays) ? [...job.recurrence_weekdays] : []);
            setIsPipeline(bookInMode ? false : Boolean(job.is_pipeline));
            setIsWip(bookInMode ? false : Boolean(job.is_wip));
            setPipelineReason(job.pipeline_reason || "");
            setIsOfficeCleaning(Boolean(job.is_office_cleaning));
            setVisibilityMode(job.visibility_mode || "team_only");
            setVisibleTo(Array.isArray(job.visible_to) ? [...job.visible_to] : []);
        } else {
            // New jobs default to pipeline mode when opened FROM the Pipeline
            // page (no date pre-populated); otherwise tomorrow at 9am.
            if (pipelineMode || wipMode) {
                setDate("");
                setTime("09:00");
            } else {
                const now = new Date(); now.setDate(now.getDate() + 1);
                setDate(now.toISOString().slice(0, 10));
                setTime("09:00");
            }
            setClientId(""); setTitle(""); setClient(""); setAddress("");
            setDuration(90); setPriority("normal"); setAssignedTo("");
            setDaysSpan(1);
            setHelperIds([]);
            setNotes(""); setNotes2(""); setChecklist([]); setChecklistCounts({}); setChecklistPhotoRequired([]); setCustomDraft(""); setRecurrence("none"); setRecurrenceWeekdays([]);
            setIsPipeline(pipelineMode);
            setIsWip(wipMode && !pipelineMode);
            setPipelineReason("");
            setIsOfficeCleaning(false);
            setVisibilityMode("team_only");
            setVisibleTo([]);
        }
        setApplyToSeries(false);
    }, [open, isEdit, job, pipelineMode, wipMode, bookInMode]);

    // Debounced clash check: whenever date/time/duration/assignee/helpers change,
    // ask the backend if any target already has an overlapping job (or holiday) on that slot.
    useEffect(() => {
        if (!open) return;
        const userIds = [assignedTo, ...helperIds].filter(Boolean);
        // Pipeline jobs have no date so clashes/holidays aren't meaningful.
        if (offSchedule || !date || !time || !duration || userIds.length === 0) {
            setClashes([]); setHolidays([]);
            return;
        }
        setClashChecking(true);
        const scheduled = new Date(`${date}T${time}:00`);
        const controller = new AbortController();
        const t = setTimeout(() => {
            api.post("/jobs/clash-check", {
                scheduled_at: scheduled.toISOString(),
                duration_minutes: Number(duration) || 60,
                user_ids: userIds,
                exclude_job_id: isEdit ? job.id : null,
            }, { signal: controller.signal })
                .then(r => {
                    setClashes(r.data.clashes || []);
                    setHolidays(r.data.holidays || []);
                })
                .catch(() => { /* silent — non-blocking */ })
                .finally(() => setClashChecking(false));
        }, 350);
        return () => { clearTimeout(t); controller.abort(); };
    }, [open, isEdit, job, date, time, duration, assignedTo, helperIds, offSchedule]);

    const pickClient = (id) => {
        setClientId(id);
        const c = clients.find(x => x.id === id);
        if (c) {
            setClient(c.name);
            if (c.address) setAddress(c.address);
        }
    };

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        // Pipeline jobs are allowed without a date. Schedule jobs still
        // require date + time as before.
        if (!offSchedule && (!date || !time)) {
            toast.error("Pick a date and time, or move it to Work in progress / Pipeline.");
            return;
        }
        setBusy(true);
        try {
            const hasDate = Boolean(date && time);
            const scheduled = hasDate ? new Date(`${date}T${time}:00`) : null;
            const payload = {
                title, client_name: client, address,
                scheduled_at: scheduled ? scheduled.toISOString() : null,
                duration_minutes: Number(duration),
                notes,
                notes2,
                checklist: checklist.filter(Boolean),
                checklist_counts: Object.fromEntries(
                    Object.entries(checklistCounts)
                        .filter(([k, v]) => checklist.includes(k) && Number(v) > 1)
                        .map(([k, v]) => [k, Math.max(1, Math.min(50, Number(v)))])
                ),
                checklist_photo_required: checklistPhotoRequired.filter(k => checklist.includes(k)),
                assigned_to: assignedTo || null,
                helper_ids: helperIds.filter(h => h && h !== assignedTo),
                priority,
                recurrence_kind: (offSchedule || recurrence === "none") ? null : recurrence,
                recurrence_weekdays: (offSchedule || recurrence !== "custom_weekdays") ? [] : recurrenceWeekdays,
                client_id: clientId || null,
                is_pipeline: isPipeline,
                is_wip: isWip,
                pipeline_reason: isPipeline ? pipelineReason.trim() : "",
                is_office_cleaning: isOfficeCleaning,
                visibility_mode: visibilityMode,
                visible_to: visibilityMode === "custom" ? visibleTo : [],
                // days_span only makes sense on CREATE for one-off jobs.
                // The backend ignores it for recurring/pipeline jobs, but we
                // still clamp to 1 client-side so the payload is honest.
                days_span: !isEdit && !offSchedule && recurrence === "none"
                    ? Math.max(1, Math.min(14, Number(daysSpan) || 1))
                    : 1,
            };
            if (isEdit) {
                const url = applyToSeries && job?.series_id
                    ? `/jobs/${job.id}?apply_to_series=true`
                    : `/jobs/${job.id}`;
                const { data } = await api.patch(url, payload);
                if ((job.is_pipeline || job.is_wip) && !offSchedule && hasDate) {
                    toast.success("Booked into the schedule");
                } else {
                    toast.success(applyToSeries ? "Series updated" : "Job updated");
                }
                onUpdated && onUpdated(data);
            } else {
                const { data } = await api.post("/jobs", payload);
                toast.success(isPipeline ? "Added to Pipeline" : isWip ? "Added to Work in progress" : "Job created");
                onCreated && onCreated(data);
            }
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    // Manager-only "Add another day" affordance while editing a one-off job.
    // Prompts for how many days to append and calls the /span/extend endpoint,
    // which auto-promotes a single-day job into a series if needed.
    const handleExtendSpan = async () => {
        if (!isEdit || !job) return;
        const raw = window.prompt("How many extra days should we add after this one? (1–7)", "1");
        if (raw === null) return;
        const n = Math.max(1, Math.min(7, parseInt(raw, 10) || 0));
        if (!n) {
            toast.error("Enter a number between 1 and 7");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/jobs/${job.id}/span/extend?count=${n}`);
            toast.success(n === 1 ? "Added another day" : `Added ${n} more days`);
            // Fetch the freshly-updated anchor so the parent list refreshes
            // with the new series_id (promotion) and consistent state.
            try {
                const { data } = await api.get(`/jobs/${job.id}`);
                onUpdated && onUpdated(data);
            } catch { /* non-fatal — the parent will refetch on close */ }
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-in-up" onClick={onClose}>
            <div
                data-testid={isEdit ? "edit-job-dialog" : "create-job-dialog"}
                className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-6 border-b border-[var(--border-soft)] sticky top-0 bg-white z-10">
                    <h3 className="font-display text-2xl font-semibold tracking-tight">
                        {isEdit ? "Edit job" : "New job"}
                    </h3>
                    <button className="p-2 rounded-full hover:bg-[var(--bg-subtle)] transition-colors"
                            onClick={onClose} aria-label="Close" data-testid="close-dialog-btn">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit} className="p-6 space-y-4">
                    {/* Recurring-series scope picker (Google-Calendar style).
                        Shown at the TOP so it's the first choice the manager
                        makes when editing a recurring occurrence — no more
                        missed checkboxes at the bottom. */}
                    {isEdit && job?.series_id && job.recurrence_kind && job.recurrence_kind !== "none" && (
                        <div
                            data-testid="series-scope-picker"
                            className="rounded-xl border border-[var(--pine)] bg-[var(--pine-light)] p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pine)]">Recurring job</span>
                                <span className="text-xs text-[var(--text-secondary)]">
                                    · {job.scheduled_at ? new Date(job.scheduled_at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : ""}
                                </span>
                            </div>
                            <div className="text-xs text-[var(--text-secondary)] mb-2">
                                Which occurrences should these edits apply to?
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <label
                                    data-testid="series-scope-one"
                                    className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                                        !applyToSeries
                                            ? "border-[var(--pine)] bg-white"
                                            : "border-[var(--border-soft)] hover:border-[var(--pine)] bg-white/60"
                                    }`}>
                                    <input type="radio" name="series_scope"
                                           checked={!applyToSeries}
                                           onChange={() => setApplyToSeries(false)}
                                           className="mt-0.5 accent-[var(--pine)]" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">Just this one occurrence</div>
                                        <div className="text-[11px] text-[var(--text-muted)]">Other dates in the series stay as they were.</div>
                                    </div>
                                </label>
                                <label
                                    data-testid="series-scope-all"
                                    className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                                        applyToSeries
                                            ? "border-[var(--pine)] bg-white"
                                            : "border-[var(--border-soft)] hover:border-[var(--pine)] bg-white/60"
                                    }`}>
                                    <input type="radio" name="series_scope"
                                           checked={applyToSeries}
                                           onChange={() => setApplyToSeries(true)}
                                           className="mt-0.5 accent-[var(--pine)]" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">All future occurrences</div>
                                        <div className="text-[11px] text-[var(--text-muted)]">Applies to every upcoming date (dates & times themselves stay per-occurrence).</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    )}

                    <PlacementPicker
                        value={isPipeline ? "pipeline" : isWip ? "wip" : "schedule"}
                        onChange={v => { setIsPipeline(v === "pipeline"); setIsWip(v === "wip"); }} />

                    {/* Office cleaning flag — used to hide the job from
                        non-owner managers and to auto-finalise the job
                        every night at 23:55. Tick this if it's an office
                        contract even when the title doesn't say "office". */}
                    <label
                        data-testid="office-cleaning-toggle-label"
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                            isOfficeCleaning
                                ? "border-[var(--pine)] bg-[var(--pine-light)]"
                                : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                        }`}>
                        <input
                            type="checkbox"
                            data-testid="office-cleaning-toggle-input"
                            className="mt-0.5 accent-[var(--pine)]"
                            checked={isOfficeCleaning}
                            onChange={e => setIsOfficeCleaning(e.target.checked)} />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">
                                This is an office cleaning job
                            </div>
                            <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                                Only Colin sees office cleaning jobs. They auto-close at end of day so paperwork stays tidy.
                            </div>
                        </div>
                    </label>

                    {/* Reason a Pipeline job hasn't been scheduled yet — only
                        shown when the dialog is in pipeline mode. Free text so
                        the manager can note "waiting on quote", "customer picking
                        date", "site access being arranged" etc. */}
                    {isPipeline && (
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">
                                Why isn't it scheduled yet?
                            </label>
                            <input
                                data-testid="pipeline-reason-input"
                                className="input-field mt-1"
                                value={pipelineReason}
                                onChange={e => setPipelineReason(e.target.value)}
                                placeholder="e.g. waiting on quote, customer choosing a date, site access…"
                                maxLength={200} />
                        </div>
                    )}

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Title</label>
                        <input data-testid="job-title-input" className="input-field mt-1"
                               value={title} onChange={e => setTitle(e.target.value)} required
                               placeholder="e.g. Weekly on-site visit" />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Client (from dossier)</label>
                        <select data-testid="job-client-picker" className="input-field mt-1"
                                value={clientId} onChange={e => pickClient(e.target.value)}>
                            <option value="">— Pick a saved client (optional) —</option>
                            {clients.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                            Picking a client auto-fills their address and shows their gate/alarm codes on the job.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Client name</label>
                            <input data-testid="job-client-input" className="input-field mt-1"
                                   value={client} onChange={e => setClient(e.target.value)} required />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Address</label>
                            <input data-testid="job-address-input" className="input-field mt-1"
                                   value={address} onChange={e => setAddress(e.target.value)} required />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">
                                Date{offSchedule && <span className="ml-1 normal-case tracking-normal text-[var(--terracotta)]">optional</span>}
                            </label>
                            <input data-testid="job-date-input" type="date" className="input-field mt-1"
                                   value={date} onChange={e => setDate(e.target.value)} required={!offSchedule} />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">
                                Time{offSchedule && <span className="ml-1 normal-case tracking-normal text-[var(--terracotta)]">optional</span>}
                            </label>
                            <select data-testid="job-time-input" className="input-field mt-1"
                                    value={time}
                                    onChange={e => setTime(e.target.value)}
                                    required={!offSchedule}>
                                {TIME_SLOTS.map(slot => (
                                    <option key={slot} value={slot}>{slot}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Duration (min)</label>
                            <input data-testid="job-duration-input" type="number" min={15} step={15} className="input-field mt-1"
                                   value={duration} onChange={e => setDuration(e.target.value)} required />
                            <div className="flex gap-2 mt-2">
                                <button
                                    type="button"
                                    data-testid="duration-half-day-btn"
                                    onClick={() => setDuration(240)}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                        Number(duration) === 240
                                            ? "bg-[var(--terracotta)] text-white border-[var(--terracotta)]"
                                            : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]"
                                    }`}
                                >
                                    Half day
                                </button>
                                <button
                                    type="button"
                                    data-testid="duration-full-day-btn"
                                    onClick={() => setDuration(480)}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                        Number(duration) === 480
                                            ? "bg-[var(--terracotta)] text-white border-[var(--terracotta)]"
                                            : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]"
                                    }`}
                                >
                                    Full day
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Priority</label>
                            <select data-testid="job-priority-select" className="input-field mt-1"
                                    value={priority} onChange={e => setPriority(e.target.value)}>
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                    </div>

                    <ClashWarning
                        checking={clashChecking}
                        clashes={clashes}
                        holidays={holidays}
                    />

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Assign to</label>
                        <select data-testid="job-assignee-select" className="input-field mt-1"
                                value={assignedTo} onChange={e => {
                                    const v = e.target.value;
                                    setAssignedTo(v);
                                    setHelperIds(prev => prev.filter(id => id !== v));
                                }}>
                            <option value="">Unassigned</option>
                            {[...operatives]
                                .sort((a, b) => {
                                    // Operatives first, then managers, then by name
                                    if (a.role !== b.role) return a.role === "operative" ? -1 : 1;
                                    return a.name.localeCompare(b.name);
                                })
                                .map(o => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}{o.role === "manager" ? " (manager)" : ""}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Other operatives on site (optional)</label>
                        <p className="text-[11px] text-[var(--text-muted)] mt-1">
                            Tick anyone else working alongside the main assignee. They'll see the job too and get a heads-up.
                        </p>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {operatives
                                .filter(o => o.id !== assignedTo)
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(o => {
                                    const checked = helperIds.includes(o.id);
                                    return (
                                        <label key={o.id}
                                               data-testid={`helper-toggle-${o.id}`}
                                               className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                                                   checked
                                                       ? "border-[var(--pine)] bg-[var(--pine-light)]"
                                                       : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                                               }`}>
                                            <input type="checkbox" checked={checked}
                                                   className="accent-[var(--pine)]"
                                                   onChange={() => {
                                                       setHelperIds(prev => prev.includes(o.id)
                                                           ? prev.filter(x => x !== o.id)
                                                           : [...prev, o.id]);
                                                   }} />
                                            {o.color && (
                                                <span className="h-2.5 w-2.5 rounded-full shrink-0"
                                                      style={{ background: o.color }} />
                                            )}
                                            <span className="truncate">{o.name}{o.role === "manager" ? " (mgr)" : ""}</span>
                                        </label>
                                    );
                                })}
                            {operatives.filter(o => o.id !== assignedTo).length === 0 && (
                                <div className="col-span-full text-xs text-[var(--text-muted)] italic">
                                    No other team members to add.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Visibility — who else in the workspace can see this
                        job. Colin (the workspace owner) always sees every
                        job regardless of what's picked here. */}
                    <div data-testid="visibility-section">
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Who can see this job?</label>
                        <p className="text-[11px] text-[var(--text-muted)] mt-1">
                            Colin always sees every job. Choose whether anyone else can see it.
                        </p>
                        <div className="mt-2 grid grid-cols-1 gap-1.5">
                            {[
                                { v: "team_only", label: "Just the team on this job", hint: "Only the assignee and the other operatives on site (recommended)" },
                                { v: "everyone", label: "Everyone in the workspace", hint: "All managers and operatives can see this job" },
                                { v: "custom", label: "Choose specific team members", hint: "Pick individuals below" },
                            ].map(opt => (
                                <label key={opt.v}
                                       data-testid={`visibility-radio-${opt.v}`}
                                       className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                                           visibilityMode === opt.v
                                               ? "border-[var(--pine)] bg-[var(--pine-light)]"
                                               : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                                       }`}>
                                    <input type="radio" name="visibility_mode"
                                           value={opt.v}
                                           checked={visibilityMode === opt.v}
                                           onChange={() => setVisibilityMode(opt.v)}
                                           className="mt-0.5 accent-[var(--pine)]" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold">{opt.label}</div>
                                        <div className="text-[11px] text-[var(--text-muted)]">{opt.hint}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        {visibilityMode === "custom" && (
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {operatives
                                    // Hide the current user (they always see
                                    // their own jobs — ticking themselves is
                                    // meaningless) AND the assignee / on-site
                                    // operatives (already covered by the job
                                    // team).
                                    .filter(o => o.id !== assignedTo
                                        && !(helperIds || []).includes(o.id)
                                        && o.id !== currentUser?.id)
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map(o => {
                                        const checked = visibleTo.includes(o.id);
                                        return (
                                            <label key={o.id}
                                                   data-testid={`visible-toggle-${o.id}`}
                                                   className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                                                       checked
                                                           ? "border-[var(--pine)] bg-[var(--pine-light)]"
                                                           : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                                                   }`}>
                                                <input type="checkbox" checked={checked}
                                                       className="accent-[var(--pine)]"
                                                       onChange={() => {
                                                           setVisibleTo(prev => prev.includes(o.id)
                                                               ? prev.filter(x => x !== o.id)
                                                               : [...prev, o.id]);
                                                       }} />
                                                {o.color && (
                                                    <span className="h-2.5 w-2.5 rounded-full shrink-0"
                                                          style={{ background: o.color }} />
                                                )}
                                                <span className="truncate">{o.name}{o.role === "manager" ? " (mgr)" : ""}</span>
                                            </label>
                                        );
                                    })}
                                {operatives.filter(o => o.id !== assignedTo && !(helperIds || []).includes(o.id) && o.id !== currentUser?.id).length === 0 && (
                                    <div className="col-span-full text-xs text-[var(--text-muted)] italic">
                                        Everyone is already on this job as an operative on site.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Multi-day one-off. Hidden in edit mode (each day is a
                        separate job — extend by creating another) and when
                        the job is recurring (they have their own repeat
                        semantics) or in the Pipeline (no dates yet). */}
                    {!isEdit && !offSchedule && recurrence === "none" && (
                        <div data-testid="days-span-block">
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Runs across how many days?</label>
                            <div className="mt-1 flex items-center gap-3">
                                <input data-testid="job-days-span-input"
                                       type="number" min={1} max={14}
                                       className="input-field w-24"
                                       value={daysSpan}
                                       onChange={e => setDaysSpan(e.target.value)} />
                                <div className="flex flex-wrap gap-1.5">
                                    {[1, 2, 3, 5, 7].map(n => (
                                        <button key={n} type="button"
                                                data-testid={`days-span-preset-${n}`}
                                                onClick={() => setDaysSpan(n)}
                                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                                                    Number(daysSpan) === n
                                                        ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                                        : "bg-white text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                                                }`}>
                                            {n === 1 ? "Just today" : n === 7 ? "Full week" : `${n} days`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <p className="text-xs text-[var(--text-muted)] mt-1">
                                {Number(daysSpan) > 1
                                    ? `We'll book ${Number(daysSpan)} consecutive daily cards starting ${date || "on the chosen date"}. Each day has its own clock-in and can be completed on its own.`
                                    : "Single-day job. Bump this up if the same work runs across several days."}
                            </p>
                        </div>
                    )}

                    {/* Edit-mode span extension: works on both single-day jobs
                        (auto-promoted to a series server-side) and existing
                        spans. Hidden for recurring jobs and dateless pipeline
                        entries where "another day" isn't meaningful yet. */}
                    {isEdit && !offSchedule && !job?.recurrence_kind && job?.scheduled_at && (
                        <div data-testid="edit-span-extend-block"
                             className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-[var(--border-soft)] bg-[var(--pine-light)]">
                            <div className="text-sm">
                                <div className="font-semibold text-[var(--pine)]">Need more days on this job?</div>
                                <div className="text-xs text-[var(--text-secondary)]">Add consecutive daily cards after this one — same time, duration & assignee.</div>
                            </div>
                            <button
                                type="button"
                                data-testid="edit-span-extend-btn"
                                onClick={handleExtendSpan}
                                disabled={busy}
                                className="ml-auto btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-50">
                                <Plus size={12} /> Add another day
                            </button>
                        </div>
                    )}


                    {!offSchedule && (
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Repeats</label>
                        <select data-testid="job-recurrence-select" className="input-field mt-1"
                                value={recurrence} onChange={e => setRecurrence(e.target.value)}>
                            <option value="none">One-off (no repeat)</option>
                            <option value="daily">Every day (7 days a week)</option>
                            <option value="weekdays">Every weekday (Mon–Fri)</option>
                            <option value="custom_weekdays">Specific weekdays… (e.g. Tue &amp; Thu)</option>
                            <option value="weekly">{date ? `Same day every week (${new Date(`${date}T00:00`).toLocaleDateString(undefined, { weekday: "long" })}s)` : "Same day every week"}</option>
                            <option value="fortnightly">Every 2 weeks (same weekday)</option>
                            <option value="monthly">{date ? `Same day every month (the ${new Date(`${date}T00:00`).getDate()})` : "Same day every month"}</option>
                        </select>
                        {recurrence === "custom_weekdays" && (
                            <div data-testid="recurrence-weekdays" className="mt-2 flex flex-wrap gap-1.5">
                                {[
                                    { i: 0, label: "Mon" },
                                    { i: 1, label: "Tue" },
                                    { i: 2, label: "Wed" },
                                    { i: 3, label: "Thu" },
                                    { i: 4, label: "Fri" },
                                    { i: 5, label: "Sat" },
                                    { i: 6, label: "Sun" },
                                ].map(({ i, label }) => {
                                    const on = recurrenceWeekdays.includes(i);
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            data-testid={`weekday-chip-${i}`}
                                            aria-pressed={on}
                                            onClick={() => setRecurrenceWeekdays(prev => (
                                                on ? prev.filter(x => x !== i) : [...prev, i].sort()
                                            ))}
                                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                                                on
                                                    ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                                    : "bg-white text-[var(--text-secondary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                                            }`}>
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                            {recurrence === "none"
                                ? "One-off — nothing else will be booked."
                                : recurrence === "custom_weekdays"
                                    ? (recurrenceWeekdays.length === 0
                                        ? "Tick the days you want the job to repeat on."
                                        : `We'll book this on every ${recurrenceWeekdays.map(i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]).join(" + ")} for the next 12 occurrences, skipping bank holidays.`)
                                    : "We'll pre-book the next 12 occurrences on the schedule and automatically skip Irish bank holidays."}
                        </p>
                    </div>
                    )}

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Notes</label>
                        <textarea data-testid="job-notes-input" className="input-field mt-1 min-h-[80px] resize-y"
                                  value={notes} onChange={e => setNotes(e.target.value)}
                                  placeholder="Access info, special instructions…" />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Additional notes</label>
                        <textarea data-testid="job-notes2-input" className="input-field mt-1 min-h-[80px] resize-y"
                                  value={notes2} onChange={e => setNotes2(e.target.value)}
                                  placeholder="Anything extra — a longer brief, follow-ups, quirks…" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">
                                Checklist
                                {checklist.length > 0 && (
                                    <span className="ml-2 normal-case tracking-normal text-[var(--text-secondary)]">· {checklist.length} attached</span>
                                )}
                            </label>
                            <Link
                                to="/settings/checklist-library"
                                data-testid="manage-library-link"
                                className="text-xs text-[var(--pine)] hover:underline"
                                onClick={() => onClose()}>
                                Manage library
                            </Link>
                        </div>
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                            Pick what applies to this job. Operatives must tick every item before they can clock out.
                        </p>

                        {libraryItems.length > 0 && (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="library-items">
                                {libraryItems.map((item) => {
                                    const checked = checklist.includes(item);
                                    const libraryRequiresPhoto = libraryPhotoRequired.includes(item);
                                    const count = Math.max(1, Number(checklistCounts[item] || 1));
                                    return (
                                        <div
                                            key={item}
                                            data-testid={`library-item-${item}`}
                                            className={`flex items-start gap-2 p-2 rounded-lg border text-sm transition-colors ${
                                                checked
                                                    ? "border-[var(--pine)] bg-[var(--pine-light)]"
                                                    : "border-[var(--border-soft)] hover:border-[var(--pine)]"
                                            }`}>
                                            <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        if (checked) {
                                                            setChecklist(checklist.filter(x => x !== item));
                                                            setChecklistPhotoRequired(checklistPhotoRequired.filter(x => x !== item));
                                                            const { [item]: _drop, ...rest } = checklistCounts;
                                                            setChecklistCounts(rest);
                                                        } else {
                                                            setChecklist([...checklist, item]);
                                                            // Carry library's photo-required flag onto this job.
                                                            if (libraryRequiresPhoto && !checklistPhotoRequired.includes(item)) {
                                                                setChecklistPhotoRequired([...checklistPhotoRequired, item]);
                                                            }
                                                        }
                                                    }}
                                                    className="mt-0.5 shrink-0"
                                                />
                                                <span className="leading-snug break-words">
                                                    {item}
                                                    {libraryRequiresPhoto && (
                                                        <span
                                                            data-testid={`library-item-photo-badge-${item}`}
                                                            className="ml-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold border border-[var(--terracotta)] bg-white text-[var(--terracotta)] whitespace-nowrap"
                                                            title="Photo proof required to tick this item">
                                                            📷 photo
                                                        </span>
                                                    )}
                                                </span>
                                            </label>
                                            {checked && (
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <button
                                                        type="button"
                                                        data-testid={`count-dec-${item}`}
                                                        onClick={() => setChecklistCounts({ ...checklistCounts, [item]: Math.max(1, count - 1) })}
                                                        disabled={count <= 1}
                                                        className="h-6 w-6 inline-flex items-center justify-center rounded-full border border-[var(--border-soft)] text-xs disabled:opacity-30 hover:border-[var(--pine)]"
                                                        aria-label="Decrease count">−</button>
                                                    <span
                                                        data-testid={`count-value-${item}`}
                                                        className="text-xs font-semibold tabular-nums w-5 text-center"
                                                        title="How many times this item needs ticking">×{count}</span>
                                                    <button
                                                        type="button"
                                                        data-testid={`count-inc-${item}`}
                                                        onClick={() => setChecklistCounts({ ...checklistCounts, [item]: Math.min(50, count + 1) })}
                                                        className="h-6 w-6 inline-flex items-center justify-center rounded-full border border-[var(--border-soft)] text-xs hover:border-[var(--pine)]"
                                                        aria-label="Increase count">+</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Items on this job that aren't in the library — custom
                            additions. Rendered as editable chips so a typo
                            can still be fixed. */}
                        {checklist.filter(c => !libraryItems.includes(c)).length > 0 && (
                            <div className="mt-3 space-y-1.5" data-testid="custom-items">
                                <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">
                                    Custom items for this job
                                </div>
                                {checklist.map((item, i) => {
                                    if (libraryItems.includes(item)) return null;
                                    const count = Math.max(1, Number(checklistCounts[item] || 1));
                                    const needsPhoto = checklistPhotoRequired.includes(item);
                                    return (
                                        <div key={i} className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
                                            <input
                                                className="input-field text-sm flex-1 min-w-0"
                                                value={item}
                                                data-testid={`custom-item-input-${i}`}
                                                onChange={e => {
                                                    const oldVal = item;
                                                    const newVal = e.target.value;
                                                    const c = [...checklist]; c[i] = newVal; setChecklist(c);
                                                    // Carry per-item state when the row is renamed.
                                                    if (checklistCounts[oldVal] !== undefined) {
                                                        const { [oldVal]: v, ...rest } = checklistCounts;
                                                        setChecklistCounts({ ...rest, [newVal]: v });
                                                    }
                                                    if (checklistPhotoRequired.includes(oldVal)) {
                                                        setChecklistPhotoRequired(checklistPhotoRequired.map(x => x === oldVal ? newVal : x));
                                                    }
                                                }}
                                                placeholder="Custom item" />
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    type="button"
                                                    data-testid={`count-dec-${item}`}
                                                    onClick={() => setChecklistCounts({ ...checklistCounts, [item]: Math.max(1, count - 1) })}
                                                    disabled={count <= 1}
                                                    className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-[var(--border-soft)] text-xs disabled:opacity-30 hover:border-[var(--pine)]"
                                                    aria-label="Decrease count">−</button>
                                                <span className="text-xs font-semibold tabular-nums w-6 text-center">×{count}</span>
                                                <button
                                                    type="button"
                                                    data-testid={`count-inc-${item}`}
                                                    onClick={() => setChecklistCounts({ ...checklistCounts, [item]: Math.min(50, count + 1) })}
                                                    className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-[var(--border-soft)] text-xs hover:border-[var(--pine)]"
                                                    aria-label="Increase count">+</button>
                                            </div>
                                            <label
                                                data-testid={`custom-photo-required-${i}`}
                                                className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-1 border cursor-pointer whitespace-nowrap transition-colors ${
                                                    needsPhoto
                                                        ? "border-[var(--terracotta)] bg-[#FFF7E6] text-[var(--terracotta)] font-semibold"
                                                        : "border-[var(--border-soft)] text-[var(--text-muted)] hover:border-[var(--terracotta)]"
                                                }`}
                                                title="Operative must attach a photo to tick this item">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only"
                                                    checked={needsPhoto}
                                                    onChange={() => {
                                                        setChecklistPhotoRequired(needsPhoto
                                                            ? checklistPhotoRequired.filter(x => x !== item)
                                                            : [...checklistPhotoRequired, item]);
                                                    }} />
                                                📷
                                            </label>
                                            <button
                                                type="button"
                                                className="btn-ghost !p-2"
                                                data-testid={`remove-custom-item-${i}`}
                                                onClick={() => {
                                                    setChecklist(checklist.filter((_, idx) => idx !== i));
                                                    const { [item]: _drop, ...rest } = checklistCounts;
                                                    setChecklistCounts(rest);
                                                    setChecklistPhotoRequired(checklistPhotoRequired.filter(x => x !== item));
                                                }}
                                                aria-label="Remove">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="mt-3 flex gap-2">
                            <input
                                data-testid="custom-item-draft"
                                className="input-field text-sm"
                                value={customDraft}
                                onChange={e => setCustomDraft(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        const v = customDraft.trim();
                                        if (v && !checklist.includes(v)) setChecklist([...checklist, v]);
                                        setCustomDraft("");
                                    }
                                }}
                                placeholder="Add a one-off item for this job and press Enter" />
                            <button
                                type="button"
                                data-testid="add-custom-item-btn"
                                className="btn-ghost inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => {
                                    const v = customDraft.trim();
                                    if (v && !checklist.includes(v)) setChecklist([...checklist, v]);
                                    setCustomDraft("");
                                }}>
                                <Plus size={14} /> Add
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-2 border-t border-[var(--border-soft)] mt-2">
                        {/* Old "Apply to whole series" checkbox lived here.
                            Replaced by the radio picker at the top of the
                            form so managers can't miss it. */}
                        <span />
                        <div className="flex justify-end gap-3">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button data-testid="submit-job-btn" type="submit" className="btn-primary" disabled={busy}>
                                {busy ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? (applyToSeries ? "Save whole series" : "Save changes") : "Create job")}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function fmtSlot(iso, minutes) {
    try {
        const d = new Date(iso);
        const start = d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
        return `${start} · ${minutes}m`;
    } catch { return iso; }
}

function fmtYMD(ymd) {
    try {
        return new Date(ymd + "T00:00:00").toLocaleDateString(undefined, {
            weekday: "short", day: "numeric", month: "short",
        });
    } catch { return ymd; }
}

function ClashWarning({ checking, clashes, holidays }) {
    const nothing = clashes.length === 0 && holidays.length === 0;
    if (checking && nothing) {
        return (
            <div data-testid="clash-checking" className="text-[11px] text-[var(--text-muted)] italic">
                Checking availability…
            </div>
        );
    }
    if (nothing) return null;
    return (
        <div
            data-testid="clash-warning"
            className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm space-y-2">
            <div className="flex items-center gap-2 font-semibold text-amber-900">
                <AlertTriangle size={16} />
                {holidays.length > 0 && clashes.length === 0
                    ? "Heads up — someone's off"
                    : clashes.length > 0 && holidays.length === 0
                        ? "This slot clashes with existing jobs"
                        : "Clash & time-off detected"}
            </div>
            {clashes.map(c => (
                <div key={`${c.user_id}-${c.job_id}`} className="flex items-start gap-2 text-amber-900" data-testid={`clash-${c.user_id}-${c.job_id}`}>
                    {c.user_color && (
                        <span className="mt-1 h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.user_color }} />
                    )}
                    <div className="min-w-0">
                        <b>{c.user_name}</b> is already {c.role === "primary" ? "on" : "helping on"}{" "}
                        <b className="break-words">"{c.job_title}"</b>
                        {c.client_name ? <> at {c.client_name}</> : null} — {fmtSlot(c.scheduled_at, c.duration_minutes)}
                    </div>
                </div>
            ))}
            {holidays.map(h => (
                <div key={`${h.user_id}-${h.start_date}`} className="flex items-start gap-2 text-amber-900" data-testid={`clash-holiday-${h.user_id}`}>
                    <CalendarOff size={14} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <b>{h.user_name}</b> is off {h.start_date === h.end_date
                            ? `on ${fmtYMD(h.start_date)}`
                            : `from ${fmtYMD(h.start_date)} to ${fmtYMD(h.end_date)}`}
                        {h.reason ? <> — {h.reason}</> : null}
                    </div>
                </div>
            ))}
            <div className="text-[11px] text-amber-800 pt-1">
                You can still save — this is just a warning.
            </div>
        </div>
    );
}

const PLACEMENTS = [
    { id: "schedule", label: "Schedule", hint: "Booked in with a date and time." },
    { id: "wip", label: "Work in progress", hint: "Partly cleaned, awaiting confirmation. Assigned operatives can still see it." },
    { id: "pipeline", label: "Pipeline", hint: "No firm date yet. Only managers see the Pipeline." },
];

function PlacementPicker({ value, onChange }) {
    const active = PLACEMENTS.find(p => p.id === value);
    return (
        <div data-testid="placement-picker">
            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)]">Where does this job go?</label>
            <div className="mt-1 grid grid-cols-3 gap-1 p-1 rounded-xl bg-[var(--bg-subtle)]">
                {PLACEMENTS.map(p => (
                    <button
                        key={p.id}
                        type="button"
                        data-testid={`placement-${p.id}`}
                        onClick={() => onChange(p.id)}
                        className={`rounded-lg px-2 py-2 text-xs sm:text-sm font-semibold transition-colors ${
                            value === p.id ? "bg-white shadow-sm text-[var(--pine)]" : "text-[var(--text-secondary)] hover:text-[var(--pine)]"
                        }`}>
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1" data-testid="placement-hint">{active?.hint}</div>
        </div>
    );
}
